import React, { useCallback, useEffect, useMemo, useState } from "react";

import Modal from "react-bootstrap/Modal";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";

import qs from "query-string";
import Select from "react-select";
import { useDebounce } from "react-use";
import useToggle from "react-use/lib/useToggle";

import { useQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useRouteMatch, Link } from "react-router-dom";

import { QUERY_USERS } from "../../../config/graphql/query";
import { DELETE_USER } from "../../../config/graphql/mutation";

import Table from "../../../components/Table";
import RoleBlock from "../../../components/RoleBlock";
import Pagination from "../../../components/Pagination";
import { useClientType } from "../../Clients/Client/hooks/useClient";
import { reactSelectCustomStyles } from "../../Employees/Employee/Information";

const limit = 10;

// Valid values for the role in the URL, in display order.
const ROLES: UserRole[] = [
  "ADMIN",
  "CLIENT_ADMIN",
  "CLIENT",
  "ACTIVITY",
  "ACTIVITY_CHECKED_IN",
  "PICK_UP",
  "SERVICE_EMPLOYEE",
  "TORO1",
];

const VERIFIED = ["yes", "no"];

// A distributor's or reseller's CLIENT_ADMIN is shown under the client type.
const useRoleLabel = () => {
  const clientType = useClientType();

  return useCallback(
    (role: UserRole) =>
      role === "CLIENT_ADMIN" &&
      (clientType === "distributor" || clientType === "reseller")
        ? clientType.toUpperCase()
        : role,
    [clientType],
  );
};

const TableRow = ({ item }: { item: IUser }) => {
  const { id, firstName, lastName, email, roles } = item;

  const roleLabel = useRoleLabel();

  const [visible, setVisible] = useToggle(false);

  const { t } = useTranslation(["user", "common"]);

  const [onDelete] = useMutation(DELETE_USER, {
    refetchQueries: ["users"],
    variables: { id },
  });

  const onBeforeDelete = () => {
    onDelete();

    return setVisible(false);
  };

  return (
    <>
      <tr>
        <th scope="row">{[firstName, lastName].filter(Boolean).join(" ")}</th>
        <td>{email}</td>
        <td>{roles.map(roleLabel).join(" ")}</td>
        <td>{item?.client?.title}</td>
        <td>
          <Badge variant={item?.emailVerified ? "success" : "secondary"}>
            {t(
              `user:users.emailVerified.${item?.emailVerified ? "yes" : "no"}`,
            )}
          </Badge>
        </td>
        <td className="text-nowrap">
          <div className="d-flex">
            <RoleBlock roles={["ADMIN", "CLIENT_ADMIN"]}>
              {
                // until we add additional field in the backend so we can know
                // these are not real users but only users used for communication
                // between the services (receptions, nameboards, postal screens) and backend(graphql)
                item?.firstName !== "API" &&
                  !item?.email.startsWith("api-gateway") && (
                    <Button
                      size="sm"
                      variant="danger"
                      className="mr-2"
                      onClick={() => setVisible(true)}
                    >
                      {t("common:delete")}
                    </Button>
                  )
              }
            </RoleBlock>
            <Link to={`/users/${id}`}>
              <Button size="sm" variant="primary">
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("user:users.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("user:users.modal.body", { firstName: item.firstName })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

type UsersQuery = {
  page?: string;
  search?: string;
  role?: string;
  verified?: string;
};

const UsersRoute = React.memo(() => {
  const match = useRouteMatch();
  const history = useHistory();
  const location = useLocation();

  const { t } = useTranslation(["user", "userRoles", "common"]);

  const roleLabel = useRoleLabel();

  const query = useMemo(
    () => qs.parse(location.search) as UsersQuery,
    [location.search],
  );

  const page = Math.max(Number(query.page) || 1, 1);
  const role = ROLES.find((option) => option === query.role);
  const verified = VERIFIED.includes(query.verified ?? "")
    ? query.verified
    : undefined;

  // A changed filter starts again on page 1.
  const setFilters = useCallback(
    (next: Partial<UsersQuery>) =>
      history.push({
        search: qs.stringify(
          { ...query, page: undefined, ...next },
          { skipEmptyString: true },
        ),
      }),
    [history, query],
  );

  const [search, setSearch] = useState(query.search ?? "");

  // Follows the URL on back and forward.
  useEffect(() => setSearch(query.search ?? ""), [query.search]);

  useDebounce(
    () => {
      if (search !== (query.search ?? "")) {
        setFilters({ search });
      }
    },
    500,
    [search],
  );

  const { data, previousData } = useQuery<{
    users: IUser[];
    usersCount: number;
    usersRoles: UserRole[];
  }>(QUERY_USERS, {
    variables: {
      search: query.search,
      filter: {
        ...(role && { roles: [role] }),
        ...(verified && { emailVerified: verified === "yes" }),
      },
      pagination: { limit, skip: (page - 1) * limit },
    },
    // Picks up what the form page changed.
    fetchPolicy: "cache-and-network",
  });

  // Keeps the old page on screen until the next one arrives.
  const result = data ?? previousData;
  const users = result?.users ?? [];
  const usersCount = result?.usersCount ?? 0;

  // Deleting the last user of the last page would leave an empty page.
  useEffect(() => {
    if (data && !data.users.length && page > 1) {
      history.replace({
        search: qs.stringify({
          ...query,
          page: Math.max(Math.ceil(data.usersCount / limit), 1),
        }),
      });
    }
  }, [data, history, page, query]);

  // Only roles this client's users hold, plus the selected one if it is gone.
  const usersRoles = result?.usersRoles;

  const roleOptions = useMemo(
    () =>
      ROLES.filter(
        (value) => usersRoles?.includes(value) || value === role,
      ).map((value) => ({
        value,
        label: t(`userRoles:options.${roleLabel(value)}`),
      })),
    [role, roleLabel, t, usersRoles],
  );

  const verifiedOptions = useMemo(
    () =>
      VERIFIED.map((value) => ({
        value,
        label: t(`user:users.filter.verified.${value}`),
      })),
    [t],
  );

  const renderUser = useCallback(
    (item: IUser) => <TableRow key={item.id} item={item} />,
    [],
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            {t("user:users.nav.users")}
          </li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-start flex-wrap">
        <form
          className="form-inline"
          onSubmit={(event) => event.preventDefault()}
        >
          <label htmlFor="users-search" className="sr-only">
            {t("user:users.filter.search")}
          </label>
          <input
            id="users-search"
            type="search"
            className="form-control mr-2 mb-3"
            style={{ width: "18rem" }}
            placeholder={t("user:users.filter.search")}
            value={search}
            onChange={({ target: { value } }) => setSearch(value)}
          />
          <div className="mr-2 mb-3" style={{ minWidth: "12rem" }}>
            <Select
              aria-label={t("user:users.filter.role")}
              placeholder={t("user:users.filter.role")}
              options={roleOptions}
              value={roleOptions.find(({ value }) => value === role) ?? null}
              onChange={(option) => setFilters({ role: option?.value })}
              styles={reactSelectCustomStyles(false)}
              isClearable
            />
          </div>
          <div className="mr-2 mb-3" style={{ minWidth: "12rem" }}>
            <Select
              aria-label={t("user:users.th.emailVerified")}
              placeholder={t("user:users.th.emailVerified")}
              options={verifiedOptions}
              value={
                verifiedOptions.find(({ value }) => value === verified) ?? null
              }
              onChange={(option) => setFilters({ verified: option?.value })}
              styles={reactSelectCustomStyles(false)}
              isClearable
            />
          </div>
        </form>
        <Link
          to={`${match.path}/new`}
          type="button"
          className="btn btn-primary mb-3"
        >
          {t("user:users.button.addUser")}
        </Link>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th scope="col">{t("user:users.th.name")}</th>
            <th scope="col">{t("user:users.th.email")}</th>
            <th scope="col">{t("user:users.th.roles")}</th>
            <th scope="col">{t("user:users.th.client")}</th>
            <th scope="col">{t("user:users.th.emailVerified")}</th>
            <th scope="col">{t("user:users.th.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {users.map(renderUser)}
          {data && !usersCount ? (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                {t("user:users.empty")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
      <Pagination documentsCount={usersCount} limit={limit} />
    </div>
  );
});

export default UsersRoute;
